import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  DataTableCellContent,
  DataTableCellTrigger,
  DrawerPinned,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  useInlineRowActionChrome,
  Input,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Separator,
  Text,
  toastManager,
  useIsMobile,
  usePopoverSheet,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";
import type { AddressSuggestion } from "#app/lib/hooks/address-search-types";
import { useAddressAutocomplete } from "#app/lib/hooks/use-address-autocomplete";
import { copyToClipboard } from "#app/lib/utils/copy-to-clipboard";
import { compositeShellProps } from "#app/lib/custom-field-utils";
import { at } from "#app/lib/utils/at";
import { InlineTriggerAction } from "#app/components/entity/row-actions";

// Hover-revealed row actions are pure CSS, not a touch-device hook.
const HOVER_ACTIONS =
  "opacity-0 transition-opacity group-hover/address:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100";

// Autocomplete goes through useAddressAutocomplete → core's
// `addressAutocomplete` query (a server-side Google Places proxy), NOT the
// browser Maps JS SDK.
//
// An in-browser Places loader here injects a SECOND Maps bootstrap:
// @vis.gl/react-google-maps' APIProvider (map-view.tsx) mounts only with the
// map, so when the map and this input mount in the same commit — exactly what
// selecting an address in global search does, since it navigates to /map AND
// opens the create-project panel — two competing bootstraps make the Maps API
// throw from its own main.js and the map canvas never initializes: a black
// screen.
//
// The server proxy also keeps the Places key server-side and shares one code
// path with global search's address results, which already resolve this way.

type AddressPropertyInputProps = Omittable<{
  wholeAddress?: string | null;
  /** Text shown for an empty non-card value. */
  emptyLabel?: string;
  /** Text tone for an empty non-card value. */
  emptyTextVariant?: React.ComponentProps<typeof Text>["variant"];
  onSelect: (wholeAddress: string) => void;
  onClear?: () => void;
  /** Optional action — when provided, renders a map-pin icon button that jumps to the map screen and centers on this address. */
  onShowOnMap?: () => void;
  /** Visual treatment. "table" is a DataTable cell — the standard
   * editable-cell trigger chrome (hover ring, brand ring while the picker is
   * open), matching every other editable column; read-only it degrades to the
   * bare row. "plain" is that bare borderless row for hosts that paint their
   * own edit chrome (the issue view's InlineValueCell). "ghost"/"outline"
   * wrap the default trigger in the bordered shell the other custom-field
   * renderers get on those surfaces (transparent vs
   * page-fill-with-hover-brighten). "field" is a form column's input: the
   * trigger itself wears the Input's face, spanning the column. "card" is the kanban card's EMPTY
   * address: the footer's icon-only outlined action opening this picker (a
   * valued card address edits inline in its token instead — see
   * `IssueCardAddress`); the press guards its bubble (the card press opens
   * the issue). */
  variant?: "default" | "table" | "plain" | "ghost" | "outline" | "field" | "card";
  /** Open the popover immediately when the control mounts into an active edit state. */
  autoOpen?: boolean;
  /** Called whenever the popover open state changes. */
  onOpenChange?: (open: boolean) => void;
  /** When true, hides the autocomplete popover trigger and the clear
   * action — but keeps copy and map-pin hover actions available so
   * read-only viewers can still copy / locate. */
  readOnly?: boolean;
  /** The phone sheet's caption. Default "Address", the built-in property;
   *  a custom address field passes its own name ("Location"). */
  title?: string;
}>;

export function AddressPropertyInput({
  wholeAddress,
  emptyLabel = "—",
  emptyTextVariant = "faint",
  onSelect,
  onClear,
  onShowOnMap,
  variant = "default",
  autoOpen = false,
  onOpenChange,
  readOnly = false,
  title = "Address",
}: AddressPropertyInputProps) {
  const [open, setOpen] = useState(autoOpen);
  // The card variant rests in the card footer's action capsule and reads its
  // chrome from it; every other variant ignores this (it resolves to the
  // standalone outline, which only the card branch renders).
  const footerChrome = useInlineRowActionChrome();
  const [popoverSide, setPopoverSide] = useState<"top" | "bottom">("bottom");
  const popoverRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState("");
  const isMobile = useIsMobile();

  useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);

  const { suggestions } = useAddressAutocomplete(query);

  const clearState = useCallback(() => {
    setQuery("");
  }, []);

  const handleSelect = useCallback(
    (suggestion: AddressSuggestion) => {
      onSelect(suggestion.description);
      clearState();
      setOpen(false);
    },
    [onSelect, clearState],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        if (!isMobile) {
          const el = popoverRef.current;
          if (el?.getBoundingClientRect) {
            const spaceBelow =
              window.innerHeight - el.getBoundingClientRect().bottom;
            setPopoverSide(spaceBelow >= 300 ? "bottom" : "top");
          }
        }
        clearState();
      }
      setOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [clearState, isMobile, onOpenChange],
  );

  const handleCopy = useCallback(async () => {
    if (!wholeAddress) return;
    const ok = await copyToClipboard(wholeAddress);
    if (ok) {
      toastManager.add({ title: "Address copied", type: "success" });
    } else {
      toastManager.add({ title: "Failed to copy address", type: "error" });
    }
  }, [wholeAddress]);

  const hasValue = !!wholeAddress;

  const buttonTrigger = (
    <Button
      ref={popoverRef}
      // Full ink: the trigger shows the property VALUE, which rests at full
      // ink like every sibling property selector. The default (32px) rung is
      // the editing-input height, so rest and edit share one rung.
      ink="foreground"
      variant={variant === "field" ? "field" : "ghost"}
      size="default"
      justify="start"
      // Sizes to its content like every sibling property selector; only the
      // table and field variants fill their column. Button's base is
      // `shrink-0`, and a one-line address can outgrow a narrow panel — the
      // shrink/min-w-0 override is what lets the Text truncate instead of
      // widening the row.
      fullWidth={variant === "table" || variant === "field"}
      className={
        variant === "table" || variant === "field"
          ? "group/address"
          : "group/address max-w-full min-w-0 shrink"
      }
    >
      <Box shrink minWidth={0}>
        <Text
          size="sm"
          weight="normal"
          variant={hasValue ? "default" : emptyTextVariant}
          truncate
        >
          {wholeAddress || emptyLabel}
        </Text>
      </Box>
      {wholeAddress && (
        <>
          <Box grow />
          {onShowOnMap && (
            <InlineTriggerAction
              className={HOVER_ACTIONS}
              icon="map-pin"
              label="Open in Map"
              onClick={onShowOnMap}
            />
          )}
          <InlineTriggerAction
            className={HOVER_ACTIONS}
            icon="copy"
            label="Copy"
            onClick={handleCopy}
          />
          {!readOnly && onClear && (
            <InlineTriggerAction
              className={HOVER_ACTIONS}
              icon="x"
              label="Clear"
              onClick={onClear}
            />
          )}
        </>
      )}
    </Button>
  );

  const triggerButton =
    variant === "card" ? (
      // Empty-property action, 1:1 with the card's icon-only footer actions:
      // a ghost inside the footer's capsule. A VALUED card address never
      // renders here — it edits inline in its token (`IssueCardAddress`).
      <Button
        variant={footerChrome.variant}
        className={footerChrome.className}
        size="icon-xs"
        shape="square"
        aria-label="Set address"
        onClick={(e) => {
          e.stopPropagation();
          handleOpenChange(true);
        }}
      >
        <Icon name="map-pin" size="md" color="muted" />
      </Button>
    ) : variant === "table" && !readOnly ? (
      <DataTableCellTrigger
        active={open}
        onClick={() => handleOpenChange(true)}
        aria-label="Edit address"
      >
        <DataTableCellContent>
          <Box shrink>
            <Text
              size="sm"
              weight="normal"
              variant={hasValue ? "default" : emptyTextVariant}
              truncate
            >
              {wholeAddress || emptyLabel}
            </Text>
          </Box>
        </DataTableCellContent>
      </DataTableCellTrigger>
    ) : variant === "table" || variant === "plain" ? (
      // Bare borderless row — "plain" hosts (the issue view's InlineValueCell)
      // paint their own edit chrome; a read-only table cell keeps the display
      // without a dead hover ring.
      <Box
        as="button"
        fill="flex"
        direction="row"
        align="center"
        paddingX="md"
        disabled={readOnly}
        onClick={readOnly ? undefined : () => handleOpenChange(true)}
      >
        <Box shrink>
          <Text
            size="sm"
            weight="normal"
            variant={hasValue ? "default" : emptyTextVariant}
            truncate
          >
            {wholeAddress || emptyLabel}
          </Text>
        </Box>
      </Box>
    ) : variant === "ghost" || variant === "outline" ? (
      // The same bordered shell sibling custom-field renderers get, so an
      // address field doesn't read borderless next to them.
      <Box {...compositeShellProps(variant)} justify="center">
        {buttonTrigger}
      </Box>
    ) : (
      buttonTrigger
    );

  // Read-only card: no dead-end press target — render nothing (the footer
  // action only exists to SET an address).
  if (readOnly && variant === "card") return null;

  // Read-only mode skips the autocomplete popover/sheet entirely.
  // The trigger above gates its onClick + clear action on `readOnly`,
  // so what's left is a non-pressable display with hover-revealed
  // copy + map-pin actions.
  if (readOnly) return triggerButton;

  const searchInput = (
    <Box direction="row" align="center" gap="sm" padding="sm">
      <Icon name="search" size="lg" color="muted" />
      <Box fill="flex">
        {/* Submission is an Enter keydown on the input. */}
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search for address..."
          autoFocus
          unstyled
          onKeyDown={(event) => {
            if (event.key === "Enter" && suggestions.length > 0) {
              event.preventDefault();
              handleSelect(at(suggestions, 0, "address suggestion"));
            }
          }}
        />
      </Box>
    </Box>
  );

  // The rows, shared by the popover (under a separator) and the phone sheet
  // (under the search band's hairline).
  const suggestionRows =
    suggestions.length > 0 ? (
      <Box
        paddingX={isMobile ? undefined : "sm"}
        paddingY="sm"
        maxHeight={isMobile ? undefined : 200}
      >
        {suggestions.map((s) => (
          <Box
            as="button"
            key={s.placeId}
            onClick={() => handleSelect(s)}
            paddingX="sm"
            paddingY="sm"
            rounded="sm"
            hoverBackground
            cursor="pointer"
          >
            {/* Wraps: a clipped address drops the unit or the city, which is
                what distinguishes two otherwise identical suggestions. */}
            <Text size="sm" align="left">
              {s.description}
            </Text>
          </Box>
        ))}
      </Box>
    ) : null;

  const suggestionsList = suggestionRows ? (
    <>
      <Separator />
      {suggestionRows}
    </>
  ) : null;

  const searchContent = (
    <>
      {searchInput}
      {suggestionsList}
    </>
  );

  // The phone sheet's field: the framed search well every phone sheet
  // wears, pinned in the sheet's band under its caption — not the popover's
  // borderless glyph-and-input line, which reads as a caption without the
  // popup frame around it.
  const phoneSearchInput = (
    <DrawerPinned>
      <InputGroup>
        <InputGroupAddon>
          <Icon name="search" />
        </InputGroupAddon>
        <InputGroupInput
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search for address..."
          autoFocus
          type="search"
          onKeyDown={(event) => {
            if (event.key === "Enter" && suggestions.length > 0) {
              event.preventDefault();
              handleSelect(at(suggestions, 0, "address suggestion"));
            }
          }}
        />
      </InputGroup>
    </DrawerPinned>
  );

  const searchContentReversed = (
    <>
      {suggestionsList}
      {searchInput}
    </>
  );

  return (
    // `responsive`: a bottom sheet on a phone, captioned "Address", the
    // framed search pinned above the suggestions.
    <Popover open={open} onOpenChange={handleOpenChange} responsive>
      <PopoverTrigger render={triggerButton} />
      {/* The card rung: a suggestion line is a whole address, longer than
          the picker rung's one-word rows hold. */}
      <PopoverPopup
        align="start"
        side={popoverSide}
        padding="none"
        sheetTitle={title}
        width="card"
      >
        <PresentationSwitch
          sheet={
            <>
              {phoneSearchInput}
              {suggestionRows}
            </>
          }
          popover={popoverSide === "top" ? searchContentReversed : searchContent}
        />
      </PopoverPopup>
    </Popover>
  );
}

/** Picks the popup's body by presentation: the sheet's framed, pinned
 *  search or the popover's inline search line. Read from inside the popup,
 *  where the sheet context is. */
function PresentationSwitch({
  sheet,
  popover,
}: {
  sheet: React.ReactNode;
  popover: React.ReactNode;
}) {
  return <>{usePopoverSheet() ? sheet : popover}</>;
}
